import { useState, memo, useEffect } from 'react'
import { useGameSettings } from '../contexts/GameSettingsContext'
import { trackResultShared } from '../services/gameAnalytics'
import { Tile } from '../types/game'

// Define interface for the tile-like objects passed from navigation
interface TileLike {
  letter: string
  state: string
}

interface ShareButtonProps {
  guesses: Tile[][] | string[] | TileLike[][]
  won: boolean
}

export const ShareButton = memo(({ guesses, won }: ShareButtonProps) => {
  const [copied, setCopied] = useState(false)
  const [hasWebShare, setHasWebShare] = useState(false)
  const { settings } = useGameSettings()

  // Check Web Share API support on mount
  useEffect(() => {
    setHasWebShare(!!navigator.share)
  }, [])

  const isTileArray = (arr: unknown[]): arr is Tile[][] => {
    return (
      arr.length > 0 &&
      Array.isArray(arr[0]) &&
      arr[0].length > 0 &&
      typeof arr[0][0] === 'object' &&
      arr[0][0] !== null &&
      'letter' in arr[0][0]
    )
  }

  const isStringArray = (arr: unknown[]): arr is string[] => {
    return arr.length > 0 && typeof arr[0] === 'string'
  }

  const generateShareText = () => {
    const resultText = won ? '🎉 I won!' : '😔 Almost had it!'

    // Filter out empty guesses
    const validGuesses = guesses.filter((row) => {
      if (isStringArray(guesses)) {
        return (row as string).trim() !== ''
      } else if (isTileArray(guesses)) {
        return (row as Tile[]).some(
          (tile) => tile.letter !== '' || tile.state !== 'empty'
        )
      }
      // Handle object array with letter and state properties
      return (row as TileLike[]).some(
        (tile) => tile.letter !== '' || tile.state !== 'empty'
      )
    })

    const guessCount = validGuesses.length

    // Handle different formats of guesses
    let emojiGrid: string

    if (isTileArray(validGuesses)) {
      // Handle Tile[][] format
      emojiGrid = validGuesses
        .map((row: Tile[]) =>
          row
            .map((tile: Tile) => {
              if (tile.state === 'correct') return '🟩'
              if (tile.state === 'wrong-location') return '🟨'
              return '⬜'
            })
            .join('')
        )
        .join('\n')
    } else if (isStringArray(validGuesses)) {
      // Handle simple string[] format - deprecated but kept for backwards compatibility
      emojiGrid = (validGuesses as string[])
        .map((guess: string) => '🟩'.repeat(guess.length || 5))
        .join('\n')
    } else {
      // Handle the new format with objects that have letter and state properties
      emojiGrid = (validGuesses as TileLike[][])
        .map((row: TileLike[]) =>
          row
            .map((tile: TileLike) => {
              if (tile.state === 'correct') return '🟩'
              if (tile.state === 'wrong-location') return '🟨'
              return '⬜'
            })
            .join('')
        )
        .join('\n')
    }

    return `ZWIZLE ${resultText}\n${guessCount}/${guesses.length} guesses\n\n${emojiGrid}\n\nCan you do better? Play on zwizle.com/play/${settings.selectedMode}`
  }

  const handleShare = async () => {
    const text = generateShareText()

    try {
      // Track the share event
      const validGuesses = guesses.filter((row) => {
        if (isStringArray(guesses)) {
          return (row as string).trim() !== ''
        } else if (isTileArray(guesses)) {
          return (row as Tile[]).some(
            (tile) => tile.letter !== '' || tile.state !== 'empty'
          )
        }
        // Handle object array with letter and state properties
        return (row as TileLike[]).some(
          (tile) => tile.letter !== '' || tile.state !== 'empty'
        )
      })

      trackResultShared(settings.selectedMode, won, validGuesses.length)

      if (navigator.share) {
        await navigator.share({
          title: 'My ZWIZLE result',
          text,
        })
      } else {
        // Fallback to clipboard with better error handling
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
          setCopied(true)
          setTimeout(() => setCopied(false), 3000)
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement('textarea')
          textArea.value = text
          textArea.style.position = 'fixed'
          textArea.style.left = '-999999px'
          textArea.style.top = '-999999px'
          document.body.appendChild(textArea)
          textArea.focus()
          textArea.select()

          try {
            document.execCommand('copy')
            setCopied(true)
            setTimeout(() => setCopied(false), 3000)
          } catch (err) {
            console.error('Fallback copy failed:', err)
            // Could show a modal with the text to manually copy
          } finally {
            document.body.removeChild(textArea)
          }
        }
      }
    } catch (err) {
      console.error('Error sharing:', err)
      // Handle user cancellation gracefully (don't show error for AbortError)
      if (err instanceof Error && err.name !== 'AbortError') {
        // Could show user-friendly error message
        console.log('Share was cancelled or failed')
      }
    }
  }

  // Dynamic button text based on platform capability
  const getButtonText = () => {
    if (copied) return 'Copied to clipboard!'
    if (hasWebShare) return 'Share Results'
    return 'Copy Results'
  }

  // Dynamic icon based on platform capability
  const getButtonIcon = () => {
    if (hasWebShare) {
      // Share icon for native sharing
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-white"
        >
          <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
          <polyline points="16 6 12 2 8 6"></polyline>
          <line x1="12" y1="2" x2="12" y2="15"></line>
        </svg>
      )
    } else {
      // Copy icon for clipboard fallback
      return (
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-white"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
        </svg>
      )
    }
  }

  return (
    <button
      onClick={handleShare}
      data-share-button="true"
      className="w-full py-3.5 rounded-lg font-medium shadow-lg flex items-center justify-center gap-2 transition-all relative overflow-hidden group"
      style={{
        background: 'linear-gradient(135deg, #ed1c24, #b01018)',
        boxShadow: '0 4px 15px rgba(237, 28, 36, 0.3)',
      }}
      title={
        hasWebShare
          ? "Share your results using your device's native sharing"
          : 'Copy your results to clipboard'
      }
    >
      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></span>

      {getButtonIcon()}

      <span className="text-white text-base relative z-10">
        {getButtonText()}
      </span>
    </button>
  )
})

ShareButton.displayName = 'ShareButton'
